import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { Form } from 'react-bootstrap';
import { BsSearch, BsXLg } from 'react-icons/bs';
import styles from './filters.module.scss';
import searchIcon from '../../icons/search.svg';
import { useDebounced } from 'v2/hooks/useDebounced';

type logicOperators = '>' | '===' | '!==';

const operator_table: {
  [key in logicOperators]: (number: number, searchText: string) => boolean;
} = {
  '>': function (number, searchText) {
    return searchText.length > number;
  },
  '!==': function (number, searchText) {
    return searchText.length !== number;
  },
  '===': function (number, searchText) {
    return searchText.length === number;
  },
};

type callBackSearchText = (search: string) => void;

interface SearchBarProps {
  onSearch: callBackSearchText;
  currentText: string;
  placeHolder: string;
  classNameBar?: string;
  classNameIcon?: string;
  classNameContainer?: string;
  showOwnIcon?: boolean;
  autoFocus?: boolean;
  lengthTextValidation?: {
    number: number;
    operator: logicOperators;
    debounce: number;
  };
}

interface SearchBarIconProps {
  showOwnIcon?: boolean;
  classNameIcon?: string;
}

const SearchBarIcon = ({ showOwnIcon, classNameIcon }: SearchBarIconProps) => {
  const classesNameIcon = classnames(classNameIcon || styles.searchIcon);
  return showOwnIcon ? (
    <img
      src={searchIcon}
      alt="search"
      className={classesNameIcon}
      draggable="false"
    />
  ) : (
    <BsSearch className={classesNameIcon} style={{ fill: '#285FCB' }} />
  );
};

const SearchBar = ({
  onSearch,
  currentText,
  placeHolder,
  classNameBar,
  classNameIcon,
  classNameContainer,
  showOwnIcon,
  autoFocus = false,
  lengthTextValidation,
}: SearchBarProps) => {
  const [visibleSearchText, setVisibleSearchText] = useState(currentText);

  const { debounce: debounceDelay = 500 } = lengthTextValidation || {};

  const validatedSearch = (value: string) => {
    if (lengthTextValidation) {
      if (
        operator_table[lengthTextValidation.operator](
          lengthTextValidation.number,
          value
        )
      ) {
        onSearch(value);
      }
    } else {
      onSearch(value);
    }
  };
  const { debounced: debounceSearch, cancel } = useDebounced(
    validatedSearch,
    debounceDelay
  );

  useEffect(() => {
    if (currentText !== visibleSearchText) {
      setVisibleSearchText(currentText);
    }
  }, [currentText]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setVisibleSearchText(value);
    debounceSearch(value);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      cancel();
      onSearch(visibleSearchText);
    }
  };

  const handleClear = () => {
    setVisibleSearchText('');
    onSearch('');
  };

  return (
    <Form.Group
      className={classnames(
        'mb-0 d-flex',
        styles.searchBoxContainer,
        classNameContainer
      )}
    >
      <Form.Control
        type="text"
        placeholder={placeHolder}
        className={classnames(classNameBar || styles.searchBox)}
        onKeyDown={handleKeyPress}
        onChange={handleChange}
        value={visibleSearchText}
        data-testid="search-bar-field"
        data-cy="search:search-bar"
        autoFocus={autoFocus}
      />
      <div className={classnames('align-self-center')}>
        {visibleSearchText ? (
          <BsXLg
            className={styles.clearIcon}
            style={{ fill: '#285FCB' }}
            onClick={handleClear}
            data-cy="search:search-clear"
          />
        ) : (
          <SearchBarIcon
            classNameIcon={classNameIcon}
            showOwnIcon={showOwnIcon}
          />
        )}
      </div>
    </Form.Group>
  );
};

export default SearchBar;
